import * as theme from 'ls-common-client/dist/themes/default';
import { createShadow, createTransition, hex2rgb, lightOrDark } from './utils';

const SIZES = {
  XXS: 'xxs',
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
  XL: 'xl',
  XXL: 'xxl',
};

const PALETTE = {
  NORMAL: 'normal',
  NORMAL_LIGHT: 'normalLight',
  NORMAL_DARK: 'normalDark',
  LIGHT: 'light',
  LIGHT_LIGHT: 'lightLight',
  LIGHT_DARK: 'lightDark',
  PRIMARY: 'primary',
  PRIMARY_LIGHT: 'primaryLight',
  PRIMARY_DARK: 'primaryDark',
  SECONDARY: 'secondary',
  SECONDARY_LIGHT: 'secondaryLight',
  SECONDARY_DARK: 'secondaryDark',
  HIGHLIGHT: 'highlight',
  HIGHLIGHT_LIGHT: 'hightlightLight',
  DISABLED: 'disabled',
  DISABLED_LIGHT: 'disabledLight',
};

const colors = {
  ...theme.colors,
  grey: '#757575',
  green: '#40a499',
  hex2rgb,
  lightOrDark,
};

const palette = {
  [PALETTE.NORMAL]: colors.tundora,
  [PALETTE.NORMAL_LIGHT]: colors.emperor,
  [PALETTE.NORMAL_DARK]: colors.firefly,

  [PALETTE.LIGHT]: colors.porcelain,
  [PALETTE.LIGHT_LIGHT]: colors.white,
  [PALETTE.LIGHT_DARK]: colors.alto,

  [PALETTE.PRIMARY]: colors.lsBlue,
  [PALETTE.PRIMARY_LIGHT]: colors.Periwinkle,
  [PALETTE.PRIMARY_DARK]: colors.persianBlue,

  [PALETTE.SECONDARY]: colors.persianGreen,
  [PALETTE.SECONDARY_LIGHT]: colors.monteCarlo,
  [PALETTE.SECONDARY_DARK]: colors.teal,

  [PALETTE.HIGHLIGHT]: colors.carolinaBlue,
  [PALETTE.HIGHLIGHT_LIGHT]: colors.veryLightBlueThree,

  [PALETTE.DISABLED]: colors.silver,
  [PALETTE.DISABLED_LIGHT]: colors.nevada,
};

const fontSize = {
  ...theme.fontSize,
  [SIZES.XXS]: '9px',
  [SIZES.XS]: '11px',
  [SIZES.SM]: '14px',
  [SIZES.MD]: '16px',
  [SIZES.LG]: '18px',
  [SIZES.XL]: '22px',
  [SIZES.XXL]: '30px',
};

const iconSize = {
  ...theme.iconSize,
};

const mediaQueries = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1024px)',
  desktop: '(min-width: 1025px)',
};

const shadow = {
  ...theme.shadow,
  searchBar: '0 2px 8px 0 rgba(0, 0, 0, 0.4)',
  textSubtlePop: '0 1px 2px rgba(0, 0, 0, 0.2)',
};

const shadows = [
  'none',
  createShadow(0, 2, 1, -1, 0, 1, 1, 0, 0, 1, 3, 0),
  createShadow(0, 3, 1, -2, 0, 2, 2, 0, 0, 1, 5, 0),
  createShadow(0, 3, 3, -2, 0, 3, 4, 0, 0, 1, 8, 0),
  createShadow(0, 2, 4, -1, 0, 4, 5, 0, 0, 1, 10, 0),
  createShadow(0, 3, 5, -1, 0, 5, 8, 0, 0, 1, 14, 0),
  createShadow(0, 3, 5, -1, 0, 6, 10, 0, 0, 1, 18, 0),
  createShadow(0, 4, 5, -2, 0, 7, 10, 1, 0, 2, 16, 1),
  createShadow(0, 5, 5, -3, 0, 8, 10, 1, 0, 3, 14, 2),
  createShadow(0, 5, 6, -3, 0, 9, 12, 1, 0, 3, 16, 2),
  createShadow(0, 6, 6, -3, 0, 10, 14, 1, 0, 4, 18, 3),
  createShadow(0, 6, 7, -4, 0, 11, 15, 1, 0, 4, 20, 3),
  createShadow(0, 7, 8, -4, 0, 12, 17, 2, 0, 5, 22, 4),
  createShadow(0, 7, 8, -4, 0, 13, 19, 2, 0, 5, 24, 4),
  createShadow(0, 7, 9, -4, 0, 14, 21, 2, 0, 5, 26, 4),
  createShadow(0, 8, 9, -5, 0, 15, 22, 2, 0, 6, 28, 5),
  createShadow(0, 8, 10, -5, 0, 16, 24, 2, 0, 6, 30, 5),
  createShadow(0, 8, 11, -5, 0, 17, 26, 2, 0, 6, 32, 5),
  createShadow(0, 9, 11, -5, 0, 18, 28, 2, 0, 7, 34, 6),
  createShadow(0, 9, 12, -6, 0, 19, 29, 2, 0, 7, 36, 6),
  createShadow(0, 10, 13, -6, 0, 20, 31, 3, 0, 8, 38, 7),
  createShadow(0, 10, 13, -6, 0, 21, 33, 3, 0, 8, 40, 7),
  createShadow(0, 10, 14, -6, 0, 22, 35, 3, 0, 8, 42, 7),
  createShadow(0, 11, 14, -7, 0, 23, 36, 3, 0, 9, 44, 8),
  createShadow(0, 11, 15, -7, 0, 24, 38, 3, 0, 9, 46, 8),
];

const text = {
  ...theme.text,
  ...colors,
};

const transition = {
  create: createTransition,
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    long: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
  timingFunction: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

const backgroundImage = {
  blueGradient: 'linear-gradient(180deg,#98b0fd -45%,#2d60fb)',
};

const themeOverrides = {
  ...theme,
  backgroundImage,
  colors,
  fontSize,
  iconSize,
  palette,
  shadow,
  shadows,
  text: {
    ...text,
    getContrastText(color) {
      return lightOrDark(color) === 'light' ? text.normal : text.text100;
    },
  },
  transition,
  mediaQueries,
};

export default themeOverrides;
export { PALETTE, SIZES };
